"use client";
import React, { useState } from "react";
import CommonCard from "../../../global/card";
import CommonTable from "../../../global/table";
import CommonDialog from "../../../global/dialog";
import { message } from "antd";
import {
    EditOutlined,
    DeleteOutlined
} from "@ant-design/icons";
import { formatDateTime } from "../../../../utils/functions";
import EnhanceAddRequestForm from "./enhanceAddRequestForm";

// React Query Hooks
import { useGenericQuery } from "../../../../queries/use-generic-query";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import { QUERY_KEYS } from "../../../../lib/query-constants";
import { useQueryClient } from "@tanstack/react-query";
import CommonSkeletonLoader from "../../../global/loading";
import NoRecordFound from "../../../global/noRecordFound";

const EnhancedRequestForms = () => {
    // State
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ show: false, id: null });
    const queryClient = useQueryClient();

    // --- Queries ---
    const {
        data: weblinkData,
        isLoading: isLoadingList
    } = useGenericQuery({
        queryKey: QUERY_KEYS.weblink.lists(),
        endpoint: "weblink"
    });

    // --- Mutations ---
    const deleteMutation = useGenericMutation({
        endpoint: "weblink/delete",
        onSuccess: () => {
            message.success("Form deleted successfully");
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.weblink.lists() });
            setDeleteModal({ show: false, id: null });
        },
        onError: (err) => {
            message.error(err?.response?.data?.error || "Error deleting form");
        }
    });


    // --- Handlers ---
    const handleCreate = () => {
        setEditingId(null);
        setShowForm(true);
    };

    const handleEdit = (id) => {
        setEditingId(id);
        setShowForm(true);
    };

    const handleDelete = async () => {
        if (!deleteModal.id) return;
        await deleteMutation.mutateAsync({ id: deleteModal.id });
    };

    // Columns for List View
    const columns = [
        {
            title: "Form Name",
            dataIndex: "formName",
            key: "formName",
            render: (text, record) => (
                <span className="text-primary font-medium cursor-pointer hover:underline" onClick={() => handleEdit(record.id)}>
                    {text}
                </span>
            )
        },
        {
            title: "Created At",
            dataIndex: "updatedAt",
            key: "updatedAt",
            render: (text) => formatDateTime(text)
        }
    ];

    const actions = [
        {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: (record) => handleEdit(record.id),
            className: "!text-primary !border-primary hover:!bg-primary hover:!text-white"
        },
        {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: (record) => setDeleteModal({ show: true, id: record.id }),
            className: "!text-red-500 !border-red-500 hover:!bg-red-500 hover:!text-white"
        }
    ];

    // Map data to include 'id'
    const data = (weblinkData?.results || []).map(item => ({ ...item, id: item._id }));

    return (
        <>
            <CommonCard
                isCreateButton={{
                    show: true,
                    text: "New Contact Request Form",
                    onclick: handleCreate
                }}
                isLoading={{
                    show: isLoadingList,
                    loaderCmp: <CommonSkeletonLoader type="table" />
                }}
            >
                {Array.isArray(data) && data?.length > 0 ? <CommonTable
                    columns={columns}
                    data={data}
                    isLoading={{ show: isLoadingList }}
                    showActions={true}
                    actions={actions}
                /> : <NoRecordFound message="No web links found." />}
            </CommonCard>

            {/* Form Dialog */}
            <EnhanceAddRequestForm
                open={showForm}
                setOpen={setShowForm}
                editingId={editingId}
            />

            {/* Delete Confirmation */}
            {deleteModal.show && (
                <CommonDialog
                    title="Delete Form"
                    description="Are you sure you want to delete this contact request form?"
                    open={deleteModal.show}
                    setOpen={(val) => setDeleteModal({ show: val, id: null })}
                    onOk={handleDelete}
                    onCancel={() => setDeleteModal({ show: false, id: null })}
                    isConfirmButtonLoading={deleteMutation.isPending}
                    okButtonTitle="Delete"
                />
            )}
        </>
    );
};

export default EnhancedRequestForms;